import { SortOrder } from '../utils/data';
import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
const defaultTableStates = {
  page: 1,
  perPage: 10,
  filters: [],
  sort: {
    order: null,
    orderBy: null
  }
};
export const useTable = ({
  page = 1,
  perPage = 10,
  filters = [],
  sort: {
    order = null,
    orderBy = null
  } = defaultTableStates.sort
} = defaultTableStates) => {
  const [states, setStates] = useState({
    page,
    perPage,
    filters,
    sort: {
      order,
      orderBy
    }
  });
  const observableFilters = useRef(filters);
  useEffect(() => {
    observableFilters.current = [...states.filters];
  }, [states.filters]);
  const filterProps = useCallback(({
    dataIndex,
    value,
    type,
    multiple = false,
    autoSubmit = false
  }) => {
    return {
      onChange: event => {
        const obfilters = observableFilters.current;
        const target = event.target;
        const targetType = target.type;
        const targetValue = target.value;
        let filters = obfilters.filter(filter => !(`${filter.dataIndex}` === `${dataIndex}` && filter.type === type));
        let updateOrNewFilter;

        if (["checkbox", "radio"].includes(targetType)) {
          const isChecked = target.checked;
          const current = obfilters.find(filter => `${filter.dataIndex}` === `${dataIndex}`);

          if (targetType === "radio") {
            updateOrNewFilter = {
              dataIndex,
              type,
              value: multiple ? [value] : value
            };
          } else {
            if (multiple) {
              let values = (current?.value).filter(v => v !== value);

              if (isChecked) {
                updateOrNewFilter = {
                  dataIndex,
                  type,
                  value: [...values, value]
                };
              } else if (values.length) {
                updateOrNewFilter = {
                  dataIndex,
                  type,
                  value: values
                };
              }
            } else if (isChecked) {
              updateOrNewFilter = {
                dataIndex,
                type,
                value: value
              };
            }
          }
        } else if (targetValue && value?.includes("{search}")) {
          updateOrNewFilter = {
            dataIndex,
            type,
            value: value.replace("{search}", targetValue)
          };
        } else if (multiple ? Array.isArray(targetValue) && targetValue.length > 0 : targetValue) {
          updateOrNewFilter = {
            dataIndex,
            type,
            value: targetValue
          };
        }

        if (updateOrNewFilter) {
          filters = [...filters, updateOrNewFilter];
        }

        observableFilters.current = filters;

        if (autoSubmit) {
          filterSubmit();
        }
      }
    }; // eslint-disable-next-line
  }, [states.filters]);
  const filterSubmit = useCallback(() => {
    setStates(prev => ({ ...prev,
      filters: [...observableFilters.current],
      page: 1
    }));
  }, []);
  const filterReset = useCallback(dataIndex => {
    let obfilters = dataIndex ? observableFilters.current : [];

    if (dataIndex) {
      obfilters = observableFilters.current = obfilters.filter(filter => `${filter.dataIndex}` !== `${dataIndex}`);
    } else {
      observableFilters.current = obfilters;
    }

    setStates(prev => ({ ...prev,
      filters: [...obfilters],
      page: 1
    }));
  }, []);
  const sortProps = useCallback(({
    dataIndex,
    sortOrder = [SortOrder.ASC, SortOrder.DESC],
    render
  }) => {
    let sort;
    const prevSort = states.sort;

    if (prevSort.orderBy !== dataIndex) {
      sort = {
        order: sortOrder[0],
        orderBy: dataIndex
      };
    } else if (prevSort.orderBy === dataIndex && sortOrder.length >= 2 && prevSort.order === sortOrder[0]) {
      sort = {
        order: sortOrder[1],
        orderBy: dataIndex
      };
    } else {
      sort = {
        order: null,
        orderBy: null
      };
    }

    return {
      "data-index": dataIndex,
      "data-order": states.sort.orderBy === dataIndex ? states.sort.order : undefined,
      "data-next-order": sort.order || undefined,
      children: render(states.sort.orderBy === dataIndex ? states.sort.order : null),
      onClick: () => {
        setStates(prev => {
          return { ...prev,
            sort
          };
        });
      }
    };
  }, [states.sort]);
  const paginateProps = useCallback((page, activable = false) => {
    return {
      "data-page": page,
      "data-active": activable ? page === states.page : undefined,
      onClick: event => {
        event.preventDefault();
        setStates(prev => ({ ...prev,
          page
        }));
      }
    };
  }, [states.page]);
  const setPerPage = useCallback(perPage => {
    setStates(prev => ({ ...prev,
      perPage,
      page: 1
    }));
  }, []);
  const setPage = useCallback(page => {
    setStates(prev => ({ ...prev,
      page
    }));
  }, []);
  const setSort = useCallback(sort => {
    setStates(prev => ({ ...prev,
      sort
    }));
  }, []);
  const setFilters = useCallback(filters => {
    setStates(prev => ({ ...prev,
      filters: Array.isArray(filters) ? filters : filters(prev.filters)
    }));
  }, []);
  return useMemo(() => {
    return { ...states,
      filterProps,
      filterSubmit,
      filterReset,
      sortProps,
      paginateProps,
      setPerPage,
      setPage,
      setSort,
      setFilters
    }; // eslint-disable-next-line
  }, [states]);
};